import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Warning,
  FileText,
  Receipt,
  Storefront,
  DownloadSimple
} from '@phosphor-icons/react';
import {
  format,
  parseISO,
  isPast,
  isToday,
  isTomorrow,
  startOfDay,
  isBefore
} from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';

const formatCurrency = (amount, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);

const formatDueDate = (date) => {
  if (!date) return null;
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  if (isPast(d)) return format(d, 'd MMM yyyy');
  return format(d, 'd MMM yyyy');
};

/** Overdue = due date is strictly before today (day granularity). Today = coming up. */
const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  const d = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
  return isBefore(startOfDay(d), startOfDay(new Date()));
};

const getSortTimestamp = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const date = typeof value === 'string' ? parseISO(value) : value;
  const time = date instanceof Date ? date.getTime() : Number.NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

const sortInvoicesSoonestFirst = (a, b) =>
  getSortTimestamp(a.dueDate) - getSortTimestamp(b.dueDate);

function MyDocumentsPage() {
  const styles = useThemeStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { wedding } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);

  const weddingId = wedding?._id;

  const handleDownloadInvoice = async (inv) => {
    try {
      setDownloadingId(inv._id);
      const res = await api.get(`/api/invoices/${inv._id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${inv.invoiceNumber || inv._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchData = useCallback(async () => {
    if (!weddingId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const [proposalsRes, invoicesRes] = await Promise.all([
        api.get(`/api/agreements/wedding/${weddingId}`),
        api.get(`/api/invoices/wedding/${weddingId}`)
      ]);
      setProposals(proposalsRes.data || []);
      setInvoices(invoicesRes.data?.invoices || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [weddingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!weddingId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Please set up your wedding to view documents.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const unpaidInvoices = invoices.filter(
    (inv) =>
      inv.status !== 'paid' &&
      inv.status !== 'cancelled' &&
      inv.status !== 'partially_paid'
  );
  const overdueInvoices = unpaidInvoices
    .filter((inv) => inv.dueDate && isOverdue(inv.dueDate))
    .sort(sortInvoicesSoonestFirst);
  const upcomingInvoices = unpaidInvoices.filter(
    (inv) => !inv.dueDate || !isOverdue(inv.dueDate)
  ).sort(sortInvoicesSoonestFirst);
  const otherInvoices = invoices.filter(
    (inv) =>
      inv.status === 'paid' ||
      inv.status === 'partially_paid' ||
      inv.status === 'cancelled'
  ).sort(sortInvoicesSoonestFirst);

  const cardSx = {
    borderRadius: 2,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s, transform 0.2s',
    '&:hover': {
      boxShadow: 2,
      transform: 'translateY(-1px)'
    }
  };

  const DocCard = ({ children, onClick, borderColor }) => (
    <Card
      sx={{
        ...cardSx,
        borderLeft: borderColor ? `4px solid ${borderColor}` : 'none',
        cursor: onClick ? 'pointer' : 'default'
      }}
      onClick={onClick}
      elevation={0}
      variant="outlined"
    >
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>{children}</CardContent>
    </Card>
  );

  const SectionHeader = ({ icon: Icon, label, count, color, first }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt: first ? 0 : 3 }}>
      {Icon && (
        <Box
          sx={{
            p: 0.75,
            borderRadius: 1.5,
            bgcolor: color ? alpha(color, 0.12) : 'action.hover',
            color: color || 'text.secondary'
          }}
        >
          <Icon size={18} weight="bold" />
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: color || 'text.secondary' }}>
        {label}
      </Typography>
      {count > 0 && (
        <Chip label={count} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 720, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 0.5 }}>
          Agreements
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Agreements and invoices from your suppliers, all in one place.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: 'primary.main' }
        }}
      >
        <Tab
          label="Agreements"
          icon={<FileText size={20} />}
          iconPosition="start"
          sx={{ gap: 1 }}
        />
        <Tab
          label="Invoices"
          icon={<Receipt size={20} />}
          iconPosition="start"
          sx={{ gap: 1 }}
        />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {proposals.length === 0 ? (
            <DocCard>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    mb: 1.5
                  }}
                >
                  <FileText size={40} weight="duotone" color={theme.palette.primary.main} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No agreements yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  When suppliers send you agreements, they’ll appear here. You can view and accept
                  them directly.
                </Typography>
              </Box>
            </DocCard>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {proposals.map((p) => (
                <DocCard
                  key={p._id}
                  onClick={() => p.publicToken && window.open(`/agreement/${p.publicToken}`, '_blank')}
                  borderColor={
                    p.status === 'accepted'
                      ? theme.palette.success.main
                      : theme.palette.primary.main
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }}
                      >
                        <Storefront size={22} weight="regular" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {p.business?.name || 'Supplier'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                          {p.status === 'accepted' ? (
                            <Chip
                              size="small"
                              label="Accepted"
                              color="success"
                              icon={<CheckCircle size={14} />}
                              sx={{ height: 22 }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Awaiting your response"
                              variant="outlined"
                              icon={<Clock size={14} />}
                              sx={{ height: 22 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {p.publicToken && (
                      <ArrowRight size={20} weight="bold" color={theme.palette.text.secondary} />
                    )}
                  </Box>
                </DocCard>
              ))}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {overdueInvoices.length > 0 && (
            <>
              <SectionHeader
                icon={Warning}
                label="Overdue"
                count={overdueInvoices.length}
                color={theme.palette.error.main}
                first
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {overdueInvoices.map((inv) => (
                  <DocCard key={inv._id} borderColor={theme.palette.error.main}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {inv.business?.name || 'Supplier'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(inv.total ?? inv.amount, inv.currency)} · Due{' '}
                          {formatDueDate(inv.dueDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label="Overdue"
                          color="error"
                          icon={<Warning size={14} />}
                          sx={{ height: 24 }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={
                            downloadingId === inv._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadSimple size={18} />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadInvoice(inv);
                          }}
                          disabled={downloadingId === inv._id}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  </DocCard>
                ))}
              </Box>
            </>
          )}

          {upcomingInvoices.length > 0 && (
            <>
              <SectionHeader
                icon={Clock}
                label="Coming up"
                count={upcomingInvoices.length}
                color={theme.palette.warning.main}
                first={overdueInvoices.length === 0}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {upcomingInvoices.map((inv) => (
                  <DocCard key={inv._id} borderColor={theme.palette.warning.main}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {inv.business?.name || 'Supplier'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(inv.total ?? inv.amount, inv.currency)} · Due{' '}
                          {formatDueDate(inv.dueDate)}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          downloadingId === inv._id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DownloadSimple size={18} />
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadInvoice(inv);
                        }}
                        disabled={downloadingId === inv._id}
                      >
                        Download
                      </Button>
                    </Box>
                  </DocCard>
                ))}
              </Box>
            </>
          )}

          {otherInvoices.length > 0 && (
            <>
              <SectionHeader
                icon={CheckCircle}
                label="Paid and completed"
                count={otherInvoices.length}
                color={theme.palette.success.main}
                first={overdueInvoices.length === 0 && upcomingInvoices.length === 0}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {otherInvoices.map((inv) => (
                  <DocCard key={inv._id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {inv.business?.name || 'Supplier'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(inv.total ?? inv.amount, inv.currency)}
                          {inv.dueDate && ` · Due ${formatDueDate(inv.dueDate)}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={inv.status}
                          color={inv.status === 'paid' ? 'success' : 'default'}
                          sx={{ height: 24, textTransform: 'capitalize' }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={
                            downloadingId === inv._id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadSimple size={18} />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadInvoice(inv);
                          }}
                          disabled={downloadingId === inv._id}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  </DocCard>
                ))}
              </Box>
            </>
          )}

          {invoices.length === 0 && (
            <DocCard>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    mb: 1.5
                  }}
                >
                  <Receipt size={40} weight="duotone" color={theme.palette.primary.main} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No invoices yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Invoices from your suppliers will appear here when sent. You can view and pay them
                  directly.
                </Typography>
              </Box>
            </DocCard>
          )}
        </Box>
      )}
    </Box>
  );
}

export default MyDocumentsPage;
